/**
 * Media inside a conversation: pictures, video clips, audio files and voice
 * notes, played in the app rather than handed to the browser.
 *
 * - Pictures hold their space before loading, show a gentle placeholder while
 *   they arrive and a clear "unavailable" state if they cannot.
 * - Video clips use the browser's own player controls (play/pause, progress,
 *   volume, duration, full screen) inside a frame, never autoplay, and have an
 *   expanded viewer. An unsupported format falls back to a download row.
 * - Audio files get a player with the file name, progress, duration, volume
 *   and download.
 * - Voice notes get a waveform, play/pause, progress, duration and a speed
 *   control.
 *
 * Only one of any of these plays at a time: starting one pauses the last.
 */

import { useEffect, useRef, useState } from 'react';
import { useBackdropClose } from '@/lib/backdrop';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { useSession } from '@/stores/session';
import type { Attachment } from '@/lib/types';

/* ---------------------------------------------------------- one at a time */

let active: HTMLMediaElement | null = null;
function claim(el: HTMLMediaElement) {
  if (active && active !== el && !active.paused) active.pause();
  active = el;
}

function fmt(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  return m + ':' + String(Math.floor(seconds % 60)).padStart(2, '0');
}

export function isVoiceNote(a: Attachment): boolean {
  return /^voice-note/i.test(a.name) && (a.type.startsWith('audio/') || a.type === 'video/webm');
}

function humanSize(bytes: number) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function download(src: string, name: string) {
  const link = document.createElement('a');
  link.href = src;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

const ROUND = 'rounded-[calc(16px*var(--radius-mul,1))]';

/* ---------------------------------------------------------------- pictures */

/** A picture that keeps its space and says so when it cannot load. */
export function SafeImage({ a, src, cover, className }: { a: Attachment; src: string; cover?: boolean; className?: string }) {
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const saving = useSession((st) => !!st.settings?.dataSaver);
  /* Data saver: the picture keeps its place in the conversation and its
     size, and is fetched when somebody asks for it. */
  const [asked, setAsked] = useState(false);
  const ratio = a.width && a.height ? a.width + ' / ' + a.height : '4 / 3';

  /*
   * Sent as a spoiler.
   *
   * The same cover as ||text||: the app's own surface rather than a black
   * rectangle, with the picture blurred underneath so it reads as something
   * withheld rather than something that failed to load.
   */
  if (a.spoiler && !asked) {
    return (
      <button
        type="button"
        style={{ aspectRatio: ratio }}
        onClick={(e) => { e.stopPropagation(); setAsked(true); }}
        className={cn('relative grid w-full place-items-center overflow-hidden bg-surface-3 text-center outline-none', className)}
      >
        <img src={src} alt="" aria-hidden className="absolute inset-0 h-full w-full scale-110 object-cover opacity-45 blur-[22px]" />
        <span className="relative flex flex-col items-center gap-1.5 px-3">
          <Icon name="eyeOff" size={22} className="text-dim" />
          <span className="text-[12.5px] font-bold tracking-[.1em] text-dim uppercase">Spoiler</span>
          <span className="text-[12px] text-mute">Click to see it</span>
        </span>
      </button>
    );
  }

  if (saving && !asked) {
    const kb = a.size ? Math.round(a.size / 1024) : 0;
    return (
      <button
        type="button"
        style={{ aspectRatio: ratio }}
        onClick={(e) => { e.stopPropagation(); setAsked(true); }}
        className={cn('grid w-full place-items-center bg-surface-3 text-center text-[13px] text-dim outline-none transition-colors hover:bg-surface-4', className)}
      >
        <span className="flex flex-col items-center gap-1.5 px-3">
          <Icon name="image" size={22} />
          <span className="font-semibold">Show picture</span>
          <span className="text-[12px] text-mute">{a.name}{kb ? ' · ' + (kb > 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb + ' KB') : ''}</span>
        </span>
      </button>
    );
  }

  if (state === 'failed') {
    return (
      <span style={{ aspectRatio: ratio }} className={cn('grid w-full place-items-center bg-surface-3 text-center text-[13px] text-mute', className)}>
        <span className="flex flex-col items-center gap-1.5 px-3">
          <Icon name="image" size={22} />
          Image unavailable
        </span>
      </span>
    );
  }

  return (
    <span style={{ aspectRatio: ratio }} className={cn('relative block w-full overflow-hidden bg-surface-3', state === 'loading' && 'animate-pulse motion-reduce:animate-none', className)}>
      <img
        src={src}
        alt={a.name}
        loading="lazy"
        decoding="async"
        width={a.width ?? undefined}
        height={a.height ?? undefined}
        onLoad={() => setState('ready')}
        onError={() => setState('failed')}
        className={cn(
          'absolute inset-0 h-full w-full transition-opacity duration-300',
          cover ? 'object-cover' : 'object-contain',
          state === 'ready' ? 'opacity-100' : 'opacity-0'
        )}
      />
    </span>
  );
}

/* ---------------------------------------------------------------- video */

/**
 * A video that has not been asked for yet.
 *
 * Data saver holds the whole file, not only its poster: a browser given a
 * <video> element fetches metadata and often the first seconds, which is the
 * part somebody on a phone plan is trying to avoid.
 */
function HeldBack({ a, onShow }: { a: Attachment; onShow: () => void }) {
  const mb = a.size ? a.size / (1024 * 1024) : 0;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onShow(); }}
      className={cn('grid w-full place-items-center rounded-[calc(16px*var(--radius-mul,1))] bg-surface-3 py-10 text-center outline-none transition-colors hover:bg-surface-4', ROUND)}
    >
      <span className="flex flex-col items-center gap-1.5 px-3">
        <Icon name="play" size={22} className="text-dim" />
        <span className="text-[13px] font-semibold text-dim">Play video</span>
        <span className="text-[12px] text-mute">{a.name}{mb ? ' · ' + mb.toFixed(1) + ' MB' : ''}</span>
      </span>
    </button>
  );
}

export function VideoAttachment({ a, src }: { a: Attachment; src: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const saving = useSession((st) => !!st.settings?.dataSaver);
  /* Data saver, or a spoiler: either way the file is not fetched until
     somebody asks for it. */
  const [asked, setAsked] = useState(false);

  if (failed) return <FileRow a={a} src={src} note="This clip cannot play here - download it instead." />;
  if ((saving || a.spoiler) && !asked) {
    return a.spoiler
      ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setAsked(true); }}
          className={cn('grid w-full max-w-[480px] place-items-center bg-surface-3 py-10 text-center outline-none transition-colors hover:bg-surface-4', ROUND)}
        >
          <span className="flex flex-col items-center gap-1.5 px-3">
            <Icon name="eyeOff" size={22} className="text-dim" />
            <span className="text-[12.5px] font-bold tracking-[.1em] text-dim uppercase">Spoiler</span>
            <span className="text-[12px] text-mute">Click to play it</span>
          </span>
        </button>
      )
      : <div className="w-full max-w-[480px]"><HeldBack a={a} onShow={() => setAsked(true)} /></div>;
  }

  return (
    <div className={cn('group relative w-full max-w-[480px] overflow-hidden bg-black', ROUND)}>
      <video
        ref={ref}
        src={src}
        controls
        preload="metadata"
        playsInline
        onPlay={(e) => claim(e.currentTarget)}
        onError={() => setFailed(true)}
        style={{ aspectRatio: a.width && a.height ? a.width + ' / ' + a.height : '16 / 9' }}
        className="block w-full bg-black"
      >
        <track kind="captions" />
      </video>
      <button
        type="button"
        aria-label="Expand video"
        title="Expand video"
        onClick={() => { ref.current?.pause(); setExpanded(true); }}
        className="absolute top-2 right-2 grid h-9 w-9 place-items-center rounded-[10px] bg-black/60 text-white opacity-0 outline-none backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <Icon name="expand" size={16} />
      </button>
      {expanded ? <VideoViewer a={a} src={src} start={ref.current?.currentTime ?? 0} onClose={() => setExpanded(false)} /> : null}
    </div>
  );
}

function VideoViewer({ a, src, start, onClose }: { a: Attachment; src: string; start: number; onClose: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); opener?.focus?.(); };
  }, [onClose]);

  /* The black around the video closes it; the video itself does not. */
  const backdrop = useBackdropClose(onClose);

  return createPortal(
    <div
      {...backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={a.name}
      className="fixed inset-0 z-[330] flex flex-col bg-black/92 animate-[fade-in_220ms_ease-out] motion-reduce:animate-none"
    >
      <div className="flex flex-none items-center gap-3 px-4 py-3 text-white">
        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">{a.name}</span>
        <button type="button" onClick={() => download(src, a.name)} className="flex h-9 items-center gap-2 rounded-full bg-white/15 px-3.5 text-[13px] font-semibold outline-none hover:bg-white/25 focus-visible:shadow-[0_0_0_2px_var(--accent)]">
          <Icon name="download" size={15} />Download
        </button>
        <button ref={closeBtn} type="button" onClick={onClose} className="flex h-9 items-center gap-2 rounded-full bg-white/15 px-3.5 text-[13px] font-semibold outline-none hover:bg-white/25 focus-visible:shadow-[0_0_0_2px_var(--accent)]">
          <Icon name="close" size={15} />Close
        </button>
      </div>
      <div className="grid min-h-0 flex-1 place-items-center p-4">
        <video
          ref={ref}
          src={src}
          controls
          playsInline
          onLoadedMetadata={(e) => { e.currentTarget.currentTime = start; }}
          onPlay={(e) => claim(e.currentTarget)}
          className="max-h-full max-w-full"
        />
      </div>
    </div>,
    document.body
  );
}

/* ---------------------------------------------------------------- audio */

function useMedia() {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);

  const bind = {
    ref,
    preload: 'metadata' as const,
    onPlay: (e: React.SyntheticEvent<HTMLAudioElement>) => { claim(e.currentTarget); setPlaying(true); },
    onPause: () => setPlaying(false),
    onEnded: () => { setPlaying(false); setTime(0); },
    onTimeUpdate: (e: React.SyntheticEvent<HTMLAudioElement>) => setTime(e.currentTarget.currentTime),
    onLoadedMetadata: (e: React.SyntheticEvent<HTMLAudioElement>) => {
      const d = e.currentTarget.duration;
      /* Recorded webm reports Infinity until it has been read through once. */
      if (Number.isFinite(d)) setDuration(d);
      else { e.currentTarget.currentTime = 1e9; }
    },
    onDurationChange: (e: React.SyntheticEvent<HTMLAudioElement>) => {
      const el = e.currentTarget;
      if (Number.isFinite(el.duration)) {
        setDuration(el.duration);
        if (el.currentTime > el.duration) el.currentTime = 0;
      }
    },
    onError: () => setFailed(true)
  };

  function toggle() {
    const el = ref.current;
    if (!el) return;
    if (el.paused) { claim(el); void el.play().catch(() => setFailed(true)); }
    else el.pause();
  }

  function seek(to: number) {
    const el = ref.current;
    if (!el || !duration) return;
    el.currentTime = Math.max(0, Math.min(duration, to));
    setTime(el.currentTime);
  }

  return { ref, bind, playing, time, duration, failed, toggle, seek };
}

function PlayButton({ playing, onClick, label, big }: { playing: boolean; onClick: () => void; label: string; big?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={(playing ? 'Pause ' : 'Play ') + label}
      className={cn(
        'grid flex-none place-items-center rounded-full bg-accent text-accent-fg outline-none transition-transform duration-100 active:scale-95 focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]',
        big ? 'h-11 w-11' : 'h-10 w-10'
      )}
    >
      <Icon name={playing ? 'pause' : 'play'} size={big ? 18 : 16} />
    </button>
  );
}

export function AudioAttachment({ a, src }: { a: Attachment; src: string }) {
  const m = useMedia();
  const [volume, setVolume] = useState(1);
  useEffect(() => { if (m.ref.current) m.ref.current.volume = volume; }, [volume, m.ref]);

  if (m.failed) return <FileRow a={a} src={src} note="This audio cannot play here - download it instead." />;

  return (
    <div className={cn('flex w-full max-w-[440px] items-center gap-3 border border-line-soft bg-surface-2 p-3', ROUND)}>
      <audio {...m.bind} src={src} />
      <PlayButton playing={m.playing} onClick={m.toggle} label={a.name} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink">{a.name}</span>
          <span className="flex-none text-[12.5px] text-mute tabular-nums">{fmt(m.time)} / {fmt(m.duration)}</span>
        </div>
        <input
          type="range"
          min={0}
          max={m.duration || 0}
          step={0.1}
          value={m.time}
          onChange={(e) => m.seek(Number(e.target.value))}
          aria-label={'Position in ' + a.name}
          className="mt-1.5 w-full accent-[var(--accent)]"
        />
      </div>
      <label className="hidden items-center gap-1 text-mute sm:flex" title="Volume">
        <Icon name="volume" size={16} />
        <span className="sr-only">Volume</span>
        <input type="range" min={0} max={1} step={0.05} value={volume} onChange={(e) => setVolume(Number(e.target.value))} className="w-16 accent-[var(--accent)]" />
      </label>
      <button type="button" onClick={() => download(src, a.name)} aria-label={'Download ' + a.name} title="Download" className="grid h-9 w-9 flex-none place-items-center rounded-[10px] text-mute outline-none hover:bg-surface-3 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]">
        <Icon name="download" size={16} />
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------- voice notes */

const waveCache = new Map<string, number[]>();

async function waveform(src: string, bars: number): Promise<number[]> {
  const hit = waveCache.get(src);
  if (hit) return hit;
  const bytes = await (await fetch(src)).arrayBuffer();
  const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  try {
    const audio = await ctx.decodeAudioData(bytes);
    const data = audio.getChannelData(0);
    const size = Math.max(1, Math.floor(data.length / bars));
    const out: number[] = [];
    for (let i = 0; i < bars; i++) {
      let sum = 0;
      for (let j = 0; j < size; j++) sum += Math.abs(data[i * size + j] ?? 0);
      out.push(sum / size);
    }
    const max = Math.max(...out, 0.0001);
    const norm = out.map((v) => Math.max(0.14, v / max));
    waveCache.set(src, norm);
    return norm;
  } finally {
    void ctx.close();
  }
}

const SPEEDS = [1, 1.5, 2];

export function VoiceNote({ a, src, tone = 'plain' }: { a: Attachment; src: string; tone?: 'plain' | 'mine' }) {
  const m = useMedia();
  const [bars, setBars] = useState<number[] | null>(null);
  const [speed, setSpeed] = useState(1);
  const [hover, setHover] = useState<number | null>(null);
  const BARS = 42;

  useEffect(() => {
    let alive = true;
    void waveform(src, BARS).then((w) => { if (alive) setBars(w); }).catch(() => { if (alive) setBars(Array.from({ length: BARS }, (_, i) => 0.3 + 0.5 * Math.abs(Math.sin(i * 0.7)))); });
    return () => { alive = false; };
  }, [src]);

  useEffect(() => { if (m.ref.current) m.ref.current.playbackRate = speed; }, [speed, m.ref]);

  if (m.failed) return <FileRow a={a} src={src} note="This voice note cannot play here - download it instead." />;

  const progress = m.duration ? m.time / m.duration : 0;
  const shown = bars ?? Array.from({ length: BARS }, (_, i) => 0.25 + 0.2 * Math.abs(Math.sin(i * 0.9)));
  const mine = tone === 'mine';

  return (
    /*
     * A voice note, as the reference draws it: a round play button, a
     * waveform that fills in as it plays (and shows where a click would land),
     * the time so far over the whole length, and the speed.
     */
    <div
      role="group"
      aria-label={'Voice note, ' + fmt(m.duration)}
      className={cn(
        'flex w-full max-w-[460px] items-center gap-3 rounded-[calc(20px*var(--radius-mul,1))] py-2 pr-2.5 pl-2',
        mine
          ? 'bg-accent text-accent-fg shadow-[0_8px_22px_-12px_color-mix(in_srgb,var(--accent)_80%,transparent)]'
          : 'bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-soft)]'
      )}
    >
      <audio {...m.bind} src={src} />
      <button
        type="button"
        onClick={m.toggle}
        aria-label={(m.playing ? 'Pause' : 'Play') + ' voice note'}
        className={cn(
          'relative grid h-11 w-11 flex-none place-items-center rounded-full outline-none transition-transform duration-150 active:scale-95 focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]',
          mine ? 'bg-accent-fg text-accent' : 'bg-accent text-accent-fg shadow-[0_6px_14px_-6px_color-mix(in_srgb,var(--accent)_80%,transparent)]'
        )}
      >
        {m.playing ? <span aria-hidden className={cn('absolute inset-0 animate-ping rounded-full opacity-25 motion-reduce:animate-none', mine ? 'bg-accent-fg' : 'bg-accent')} /> : null}
        <Icon name={m.playing ? 'pause' : 'play'} size={17} className={m.playing ? '' : 'translate-x-[1px]'} />
      </button>
      <div
        role="slider"
        tabIndex={0}
        aria-label="Voice note position"
        aria-valuemin={0}
        aria-valuemax={Math.round(m.duration)}
        aria-valuenow={Math.round(m.time)}
        aria-valuetext={fmt(m.time) + ' of ' + fmt(m.duration)}
        onPointerMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHover((e.clientX - r.left) / r.width); }}
        onPointerLeave={() => setHover(null)}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          m.seek(((e.clientX - r.left) / r.width) * m.duration);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); m.seek(m.time + 2); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); m.seek(m.time - 2); }
          if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); m.toggle(); }
        }}
        className="flex h-10 min-w-[90px] flex-1 cursor-pointer items-center gap-[2.5px] rounded-[8px] outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        {shown.map((h, i) => {
          const at = i / shown.length;
          const played = at <= progress && progress > 0;
          const hinted = hover !== null && at <= hover;
          return (
            <span
              key={i}
              aria-hidden
              style={{ height: Math.max(14, Math.round(h * 100)) + '%', transitionDelay: m.playing ? '0ms' : Math.min(i, 30) * 6 + 'ms' }}
              className={cn(
                'w-[3px] flex-1 rounded-full transition-[background-color,opacity,transform] duration-200',
                mine
                  ? played ? 'bg-accent-fg' : hinted ? 'bg-accent-fg/70' : 'bg-accent-fg/40'
                  : played ? 'bg-accent' : hinted ? 'bg-[color-mix(in_srgb,var(--accent)_60%,transparent)]' : 'bg-[color-mix(in_srgb,var(--accent)_28%,var(--line))]',
                bars ? 'opacity-100' : 'animate-pulse opacity-60'
              )}
            />
          );
        })}
      </div>
      <span className={cn('flex-none text-[13px] font-medium tabular-nums', mine ? 'text-accent-fg/85' : 'text-dim')}>
        {fmt(m.time)} / {fmt(m.duration)}
      </span>
      <button
        type="button"
        onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]!)}
        aria-label={'Playback speed ' + speed + ' times'}
        title="Playback speed"
        className={cn(
          'h-8 min-w-[40px] flex-none rounded-[10px] px-2 text-[12.5px] font-bold outline-none transition-colors duration-200 focus-visible:shadow-[0_0_0_2px_var(--accent)]',
          mine ? 'bg-accent-fg/15 text-accent-fg hover:bg-accent-fg/25' : 'bg-surface-1 text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] hover:text-ink'
        )}
      >
        {speed}x
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------- fallback */

function FileRow({ a, src, note }: { a: Attachment; src: string; note: string }) {
  return (
    <div className={cn('flex w-full max-w-[440px] items-center gap-3 border border-line-soft bg-surface-2 p-3', ROUND)}>
      <span className="grid h-10 w-10 flex-none place-items-center rounded-[10px] bg-surface-3 text-accent"><Icon name="download" size={17} /></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-ink">{a.name}</span>
        <span className="block text-[12.5px] text-mute">{humanSize(a.size)} · {note}</span>
      </span>
      <button type="button" onClick={() => download(src, a.name)} aria-label={'Download ' + a.name} className="grid h-9 w-9 flex-none place-items-center rounded-[10px] text-mute outline-none hover:bg-surface-3 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]">
        <Icon name="download" size={16} />
      </button>
    </div>
  );
}
